import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { LOGOS } from '../../assets/logos';
import * as FiIcons from 'react-icons/fi';

const { FiMic, FiVideo, FiCheck, FiX, FiRepeat, FiAlertTriangle } = FiIcons;

const MediaTest = ({ onComplete }) => {
  const [videoStream, setVideoStream] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [videoTested, setVideoTested] = useState(false);
  const [audioTested, setAudioTested] = useState(false);
  const [error, setError] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const animationRef = useRef(null);

  const startVideoTest = async () => {
    try {
      setIsVideoLoading(true);
      setError(null);
      
      // Release any previous stream
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }

      console.log("Requesting video stream with updated constraints");
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          facingMode: "user",
          frameRate: { ideal: 30 }
        }
      });

      console.log("Video stream obtained:", stream);
      setVideoStream(stream);

      if (videoRef.current) {
        console.log("Setting up video element");
        
        // Clear any previous source
        videoRef.current.srcObject = null;
        
        // Wait a moment then set the new stream
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.autoplay = true;
            videoRef.current.playsInline = true;
            videoRef.current.muted = true;
            
            // Force styles
            videoRef.current.style.display = 'block';
            videoRef.current.style.width = '100%';
            videoRef.current.style.height = '100%';
            videoRef.current.style.objectFit = 'cover';
            videoRef.current.style.transform = 'scaleX(-1)';
            
            videoRef.current.onloadedmetadata = () => {
              console.log("Video metadata loaded");
              videoRef.current.play()
                .then(() => {
                  console.log("Video playing successfully");
                  setIsVideoLoading(false);
                  setError(null);
                })
                .catch(e => {
                  console.error("Error playing video:", e);
                  setError(`Error al reproducir el video: ${e.message}`);
                  setIsVideoLoading(false);
                });
            };
            
            videoRef.current.onerror = (e) => {
              console.error("Video element error:", e);
              setError("Error en el elemento de video");
              setIsVideoLoading(false);
            };
          }
        }, 100);
      }
    } catch (err) {
      console.error("Camera access error:", err);
      setIsVideoLoading(false);
      if (err.name === 'NotAllowedError') {
        setError('Acceso a la cámara denegado. Por favor, permite el acceso e intenta de nuevo.');
      } else if (err.name === 'NotFoundError') {
        setError('No se encontró cámara. Verifica que tengas una cámara conectada.');
      } else {
        setError(`No se pudo acceder a la cámara: ${err.message}`);
      }
    }
  };

  const startAudioTest = async () => {
    try {
      // Release any previous stream
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }

      console.log("Requesting audio stream");
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      setAudioStream(stream);

      // Create AudioContext for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyzer = audioContext.createAnalyser();
      source.connect(analyzer);

      analyzer.fftSize = 256;
      const bufferLength = analyzer.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const draw = () => {
        if (!audioTested) {
          animationRef.current = requestAnimationFrame(draw);
          analyzer.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / bufferLength;
          setAudioLevel(average);
          
          if (average > 30) {
            if (audioRef.current) {
              audioRef.current.style.transform = `scale(${1 + average/500})`;
            }
          }
        }
      };
      
      draw();
      setError(null);
    } catch (err) {
      console.error("Microphone access error:", err);
      if (err.name === 'NotAllowedError') {
        setError('Acceso al micrófono denegado. Por favor, permite el acceso e intenta de nuevo.');
      } else {
        setError(`No se pudo acceder al micrófono: ${err.message}`);
      }
    }
  };

  const stopMediaTracks = () => {
    console.log("Stopping all media tracks");
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
    }
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  useEffect(() => {
    return () => stopMediaTracks();
  }, []);

  const handleComplete = () => {
    stopMediaTracks();
    onComplete();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
        Prueba de Cámara y Micrófono
      </h2>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 flex items-start space-x-2">
          <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Error detectado:</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Video Test */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <SafeIcon icon={FiVideo} className="w-5 h-5 mr-2" />
            Prueba de Cámara
            {videoTested && (
              <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 ml-2" />
            )}
          </h3>

          <div className="relative bg-gray-900 rounded-lg aspect-video overflow-hidden">
            {isVideoLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p>Iniciando cámara...</p>
                </div>
              </div>
            )}
            
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
            
            {/* Logo VIXEA */}
            <div className="absolute bottom-4 left-4 flex items-center space-x-2">
              <img src={LOGOS.WHITE_TRANSPARENT} alt="VIXEA" className="h-8" />
            </div>

            {/* REC indicator */}
            {videoStream && !isVideoLoading && (
              <div className="absolute top-4 right-4 flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-white text-sm font-medium">LIVE</span>
              </div>
            )}

            {/* No video message */}
            {!videoStream && !isVideoLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-white">
                <div className="text-center">
                  <SafeIcon icon={FiVideo} className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Haz clic en "Probar Cámara"</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-center space-x-4">
            {!videoTested ? (
              <>
                <button
                  onClick={startVideoTest}
                  disabled={isVideoLoading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  <SafeIcon icon={FiVideo} className="w-4 h-4" />
                  <span>{isVideoLoading ? 'Iniciando...' : 'Probar Cámara'}</span>
                </button>
                {videoStream && !isVideoLoading && (
                  <button
                    onClick={() => setVideoTested(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Confirmar
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={() => {
                  setVideoTested(false);
                  startVideoTest();
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiRepeat} className="w-4 h-4" />
                <span>Repetir Prueba</span>
              </button>
            )}
          </div>
        </div>

        {/* Audio Test */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <SafeIcon icon={FiMic} className="w-5 h-5 mr-2" />
            Prueba de Micrófono
            {audioTested && (
              <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 ml-2" />
            )}
          </h3>

          <div className="bg-gray-50 rounded-lg p-6 text-center">
            <div
              ref={audioRef}
              className={`w-16 h-16 ${
                audioStream ? 'bg-primary' : 'bg-gray-300'
              } rounded-full mx-auto mb-4 flex items-center justify-center transition-transform`}
              style={audioStream ? { transform: `scale(${1 + audioLevel/500})` } : {}}
            >
              <SafeIcon
                icon={FiMic}
                className={`w-8 h-8 text-white ${audioStream ? 'animate-pulse' : ''}`}
              />
            </div>

            {audioStream ? (
              <div>
                <p className="text-gray-600 mb-2">Habla para probar el micrófono</p>
                <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-primary h-2 transition-all duration-100"
                    style={{ width: `${Math.min(audioLevel * 2, 100)}%` }}
                  ></div>
                </div>
              </div>
            ) : (
              <p className="text-gray-600">Haz clic en Probar Micrófono</p>
            )}
          </div>

          <div className="flex justify-center space-x-4">
            {!audioTested ? (
              <>
                <button
                  onClick={startAudioTest}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <SafeIcon icon={FiMic} className="w-4 h-4" />
                  <span>Probar Micrófono</span>
                </button>
                {audioStream && (
                  <button
                    onClick={() => setAudioTested(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Confirmar
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={() => {
                  setAudioTested(false);
                  startAudioTest();
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiRepeat} className="w-4 h-4" />
                <span>Repetir Prueba</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Asegúrate de que tu cámara y micrófono funcionen correctamente
          </p>
          <button
            onClick={handleComplete}
            disabled={!videoTested || !audioTested}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continuar
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default MediaTest;